import React, { useEffect, useMemo, useState } from 'react';
import { buildUiHelpers } from './sharedUi';
import { formatDate, formatDateTime } from './sharedFormatters';
import CustomRslWatchlistsSection from './CustomRslWatchlistsSection';

export default function ScreenerSection({
  cachedAt,
  cachedAtByMarket,
  marketDataAsOf,
  statusMsg,
  ergebnis,
  laden,
  screenerTabs,
  screenerTab,
  setScreenerTab,
  onScreenerTabSelect,
  activeScreenerTab,
  depot,
  kaufen,
  entfernen,
  speichernDepotPosition,
  addDepotTranche,
  deleteDepotTranche,
  tickerInZwischenablage,
  uiSettings,
  themeMode,
  COLORS,
  th,
  td,
  inputStyle,
  ScreenerTableComponent,
  canLoadMoreTopSignals,
  showLoadMoreTopSignals,
  maxTopSignalsVisible,
  onBlockedFeature,
  customWatchlistsEnabled,
  customWatchlists,
  customWatchlistsLoading,
  customWatchlistsError,
  createCustomWatchlist,
  updateCustomWatchlist,
  deleteCustomWatchlist,
  addCustomWatchlistItem,
  deleteCustomWatchlistItem,
  runCustomWatchlists,
  reportCustomWatchlistIssues,
  previewTradingViewWatchlist,
  importTradingViewWatchlist,
  isAdmin,
}) {
  const { Card } = useMemo(() => buildUiHelpers({ COLORS, th: {} }), [COLORS]);
  const ScreenerTabelle = ScreenerTableComponent;
  const currentMarketDataAsOf = marketDataAsOf?.[activeScreenerTab?.key] || null;
  const currentCachedAt = cachedAtByMarket?.[activeScreenerTab?.key] || cachedAt || null;
  const [visibleTopRowsByMarket, setVisibleTopRowsByMarket] = useState({});
  const activeMarketKey = activeScreenerTab?.key || 'SP500';
  const activeRows = Array.isArray(activeScreenerTab?.rows) ? activeScreenerTab.rows : [];
  const effectiveMaxTopRows = Number.isFinite(maxTopSignalsVisible)
    ? Math.max(10, Number(maxTopSignalsVisible))
    : activeRows.length;
  const activeVisibleRows = visibleTopRowsByMarket[activeMarketKey] || 10;
  const visibleTopRowsCount = Math.min(activeVisibleRows, effectiveMaxTopRows, activeRows.length);
  const hasMoreTopRows = !!canLoadMoreTopSignals && activeRows.length > visibleTopRowsCount;
  const visibleTopRows = activeRows.slice(0, visibleTopRowsCount);
  const shouldShowLoadMoreButton = Boolean(showLoadMoreTopSignals) && (
    hasMoreTopRows || (!canLoadMoreTopSignals && activeRows.length >= 10)
  );

  useEffect(() => {
    setVisibleTopRowsByMarket(current => {
      if (current[activeMarketKey]) return current;
      return { ...current, [activeMarketKey]: 10 };
    });
  }, [activeMarketKey]);

  const handleLoadMoreTopRows = () => {
    if (!canLoadMoreTopSignals) {
      onBlockedFeature?.('Scanner: +10 weitere laden');
      return;
    }
    setVisibleTopRowsByMarket(current => {
      const currentValue = current[activeMarketKey] || 10;
      const nextValue = Math.min(currentValue + 10, effectiveMaxTopRows, activeRows.length);
      return { ...current, [activeMarketKey]: nextValue };
    });
  };

  return (
    <div>
      {statusMsg && (
        <div style={{ color: COLORS.muted, marginBottom: '16px', fontSize: '14px' }}>
          ⏳ {statusMsg}
        </div>
      )}

      {ergebnis && (
        <Card>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', flex: '1 1 auto' }}>
              {screenerTabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => {
                    if (onScreenerTabSelect) onScreenerTabSelect(tab.key);
                    else setScreenerTab(tab.key);
                  }}
                  style={{
                    background: screenerTab === tab.key ? COLORS.accent : '#ffffff08',
                    color: screenerTab === tab.key ? '#fff' : COLORS.muted,
                    border: `1px solid ${screenerTab === tab.key ? COLORS.accent : COLORS.border}`,
                    borderRadius: '10px',
                    padding: '10px 14px',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: 'pointer',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {(currentCachedAt || currentMarketDataAsOf) && (
              <div style={{
                marginLeft: 'auto',
                flex: '0 1 420px',
                minWidth: '260px',
                padding: '7px 10px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '12px',
                background: '#ffffff06',
                color: COLORS.muted,
                fontSize: '11px',
                lineHeight: 1.45,
                textAlign: 'right',
              }}>
                {currentCachedAt && <div>Cache aktualisiert: {formatDateTime(currentCachedAt)}</div>}
                {currentMarketDataAsOf && (
                  <div>Kursstand letzter verfügbarer Tagesstand {activeScreenerTab?.label}: {formatDate(currentMarketDataAsOf)}</div>
                )}
              </div>
            )}
          </div>
          <ScreenerTabelle
            titel={visibleTopRows}
            marktLabel={activeScreenerTab.label}
            marktKey={activeScreenerTab.key}
            currency={activeScreenerTab.currency}
            depot={depot}
            onKaufen={kaufen}
            onEntfernen={entfernen}
            onSpeichernPosition={speichernDepotPosition}
            onAddTranche={addDepotTranche}
            onDeleteTranche={deleteDepotTranche}
            onCopyTickers={tickerInZwischenablage}
            chartsEnabled={uiSettings.tradingview_charts_enabled}
            themeMode={themeMode}
            COLORS={COLORS}
            th={th}
            td={td}
            inputStyle={inputStyle}
            showLoadMoreTopSignals={shouldShowLoadMoreButton}
            hasMoreTopRows={hasMoreTopRows}
            onLoadMoreTopRows={handleLoadMoreTopRows}
          />
          <div style={{ marginTop: '18px', fontSize: '13px', color: COLORS.muted, lineHeight: 1.7 }}>
            <div style={{ fontWeight: '700', color: COLORS.text, marginBottom: '6px' }}>Kurzanleitung Scanner</div>
            <div>1. Starte mit den stärksten Signalen und wähle bevorzugt die ersten 5-7 Titel aus dem Scanner.</div>
            <div>2. Achte auf Streuung: Verteile dein Kapital auf unterschiedliche Branchen und vermeide Klumpenrisiken.</div>
            <div>3. Beim Neustart werden alle ausgewählten Titel auf einmal gekauft; jeder Titel wird gleich groß gewichtet.</div>
            <div>4. Wechselt eine Position im Depot auf Rot, wird sie durch den jeweils neuen Top-Titel aus dem Scanner ersetzt.</div>
            <div>5. Der Austausch erfolgt nur montags und nur dann, wenn die Börsen geöffnet sind.</div>
          </div>
        </Card>
      )}

      {!ergebnis && !laden && (
        <Card>
          <div style={{ textAlign: 'center', padding: '48px', color: COLORS.muted }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
            <p>Die Top-Signale werden automatisch geladen.</p>
          </div>
        </Card>
      )}
      {customWatchlistsEnabled && (
        <CustomRslWatchlistsSection
          watchlists={customWatchlists}
          loading={customWatchlistsLoading}
          error={customWatchlistsError}
          createWatchlist={createCustomWatchlist}
          updateWatchlist={updateCustomWatchlist}
          deleteWatchlist={deleteCustomWatchlist}
          addItem={addCustomWatchlistItem}
          deleteItem={deleteCustomWatchlistItem}
          runWatchlists={runCustomWatchlists}
          reportIssues={reportCustomWatchlistIssues}
          previewTradingView={previewTradingViewWatchlist}
          importTradingView={importTradingViewWatchlist}
          isAdmin={isAdmin}
          depot={depot}
          kaufen={kaufen}
          entfernen={entfernen}
          speichernDepotPosition={speichernDepotPosition}
          addDepotTranche={addDepotTranche}
          deleteDepotTranche={deleteDepotTranche}
          tickerInZwischenablage={tickerInZwischenablage}
          uiSettings={uiSettings}
          themeMode={themeMode}
          COLORS={COLORS}
          th={th}
          td={td}
          inputStyle={inputStyle}
          ScreenerTableComponent={ScreenerTableComponent}
        />
      )}
    </div>
  );
}
